import { useCallback, useEffect, useRef, useState } from 'react';
import { IProcessedMeasurement } from 'app/types/measurement';
import { IWebSocketMeasurementConfig } from '../components/form/types';
import { useWebSocketMeasurementService } from './useWebSocketMeasurementService';
import { Telemetry } from 'app/services/telemetry/Telemetry';

const DEFAULT_TIMEOUT_MS = 30_000;

interface IUseFlowWizardWebSocketParams {
  webSocketConfig?: IWebSocketMeasurementConfig;
  steps: any[];
  onNavigateToResult: (stepIndex: number) => void;
}

export const useFlowWizardWebSocket = ({
  webSocketConfig,
  steps,
  onNavigateToResult,
}: IUseFlowWizardWebSocketParams) => {
  const [isWaitingForMeasurement, setIsWaitingForMeasurement] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [hasDeviceError, setHasDeviceError] = useState(webSocketConfig?.hasDeviceError ?? false);
  const [receivedMeasurement, setReceivedMeasurement] = useState<IProcessedMeasurement | null>(null);
  const hasAutoStarted = useRef(false);
  const seenMeasurementIdsRef = useRef<Set<string>>(new Set());
  const errorHighWaterRef = useRef(0);
  const mismatchReportedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Always call unconditionally — WebSocketMeasurementProvider is mounted above all tab screens.
  const webSocketService = useWebSocketMeasurementService();

  const clearMeasurementTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startMeasurementTimeout = useCallback(() => {
    clearMeasurementTimeout();
    const timeoutMs = webSocketConfig?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    timeoutRef.current = setTimeout(() => {
      setIsWaitingForMeasurement(false);
      setIsTimedOut(true);
      Telemetry.event('measurement.flow.timeout', {
        domain: 'measurement',
        activityName: webSocketConfig?.activityName,
        timeoutMs,
      }, 'warning');
    }, timeoutMs);
  }, [webSocketConfig?.activityName, webSocketConfig?.timeoutMs, clearMeasurementTimeout]);

  const beginListening = useCallback(() => {
    seenMeasurementIdsRef.current = new Set(webSocketService.measurements.map(measurement => measurement.id));
    errorHighWaterRef.current = Date.now();
    mismatchReportedRef.current = false;
    setIsWaitingForMeasurement(true);
    setIsTimedOut(false);
    setHasDeviceError(false);
    if (!webSocketService.isConnected) webSocketService.connect();
    startMeasurementTimeout();
  }, [webSocketService, startMeasurementTimeout]);

  useEffect(() => {
    if (webSocketConfig?.autoStart && !hasAutoStarted.current && !isWaitingForMeasurement) {
      hasAutoStarted.current = true;
      beginListening();
    }
  }, [webSocketConfig?.autoStart, isWaitingForMeasurement, beginListening]);

  const consumeMatchingFrame = useCallback(<T extends { activityName: string }>(
    feed: T[],
    isFresh: (frame: T) => boolean,
  ): T | null => {
    if (!isWaitingForMeasurement || !webSocketConfig) return null;

    const match = feed.find(frame => frame.activityName === webSocketConfig.activityName && isFresh(frame));
    if (!match) return null;

    clearMeasurementTimeout();
    setIsWaitingForMeasurement(false);
    setIsTimedOut(false);

    return match;
  }, [isWaitingForMeasurement, webSocketConfig, clearMeasurementTimeout]);

  useEffect(() => {
    const error = consumeMatchingFrame(
      webSocketService.measurementErrors,
      frame => frame.receivedAt > errorHighWaterRef.current,
    );
    if (error) {
      setHasDeviceError(true);
      return;
    }

    const latest = consumeMatchingFrame(
      webSocketService.measurements,
      frame => !seenMeasurementIdsRef.current.has(frame.id),
    );

    if (!latest) {
      if (!isWaitingForMeasurement || mismatchReportedRef.current) return;
      const unmatched = webSocketService.measurements.filter(
        measurement => !seenMeasurementIdsRef.current.has(measurement.id),
      );
      if (unmatched.length === 0) return;
      mismatchReportedRef.current = true;
      Telemetry.event('measurement.flow.frame.activity_mismatch', {
        domain: 'measurement',
        expected: webSocketConfig?.activityName,
        received: unmatched.map(measurement => measurement.activityName).join(','),
      }, 'warning');
      return;
    }

    setReceivedMeasurement(latest);

    const resultKey = webSocketConfig?.resultStepKey ?? 'measurementResultStep';
    const resultStepIndex = steps.findIndex(step => step.key === resultKey);

    webSocketConfig?.onMeasurementReceived?.(latest, resultStepIndex !== -1 ? steps[resultStepIndex] : null);
    if (resultStepIndex !== -1) onNavigateToResult(resultStepIndex);
  }, [webSocketService.measurementErrors, webSocketService.measurements, consumeMatchingFrame, isWaitingForMeasurement, webSocketConfig, steps, onNavigateToResult]);

  useEffect(() => () => clearMeasurementTimeout(), [clearMeasurementTimeout]);

  const startWebSocketListening = useCallback(() => {
    if (!webSocketConfig || isWaitingForMeasurement) return;
    beginListening();
  }, [webSocketConfig, isWaitingForMeasurement, beginListening]);

  const retryWebSocketListening = startWebSocketListening;

  const clearDeviceError = useCallback(() => setHasDeviceError(false), []);

  const cancelWebSocketListening = useCallback(() => {
    clearMeasurementTimeout();
    setIsWaitingForMeasurement(false);
    setIsTimedOut(false);
  }, [clearMeasurementTimeout]);

  const shouldStartListeningOnNext = useCallback((currentStepKey: string): boolean => {
    if (!webSocketConfig || isWaitingForMeasurement) return false;
    const beforeKey = webSocketConfig.beforeMeasurementStepKey ?? 'beforeMeasurementStep';
    return currentStepKey === beforeKey;
  }, [webSocketConfig, isWaitingForMeasurement]);

  return {
    isWaitingForMeasurement,
    isTimedOut,
    hasDeviceError,
    clearDeviceError,
    receivedMeasurement,
    startWebSocketListening,
    retryWebSocketListening,
    cancelWebSocketListening,
    shouldStartListeningOnNext,
    webSocketService,
  };
};
